import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { parseSystemDocumentEntries } from 'features/knowledgeDocumentViewer/types/systemDocumentEntrySchemas'
import { systemDocumentsByDocumentIds } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import {
  pipelineConfigSuffix,
  unscopeDocumentIdFromPipelineConfig,
} from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'

type PipelineConfigEntry = {
  entry: {
    id: string
    data?: { status?: string; name?: string | null }
  }
}

/** Whether a document exists under one specific pipeline config for a site. */
export interface ConfigAvailability {
  configId: string
  status: string | undefined
  isAvailable: boolean
}

export interface DocumentConfigAvailability {
  bareId: string
  providedConfigId: string | undefined
  perConfig: ConfigAvailability[]
  availableConfigs: ConfigAvailability[]
  isLoading: boolean
}

/**
 * Probes which of a site's pipeline configs a document is available under:
 * one entry per bare `DOC-<hash>` id, belonging to whichever config its
 * `pipeline-config` tag names. Ids using the retired `_<configSuffix>`
 * convention are first reduced to their bare form. Pass `documentId: undefined`
 * to disable the probe entirely.
 */
export function useDocumentConfigAvailability(
  documentId: string | undefined,
  siteID: string | undefined
): DocumentConfigAvailability {
  const { data: configs, isLoading: isLoadingConfigs } =
    useGetRolodexEntriesByLoadOptions<PipelineConfigEntry[]>(
      documentId && siteID
        ? { type: ['pipeline-config'], tags: { site: siteID }, limit: 50 }
        : undefined
    )

  const bareId = useMemo(() => {
    if (!documentId) return ''
    return (configs ?? []).reduce(
      (id, config) => unscopeDocumentIdFromPipelineConfig(id, config.entry.id),
      documentId
    )
  }, [documentId, configs])

  const providedConfigId = useMemo(() => {
    if (!documentId) return undefined
    return (configs ?? []).find((config) =>
      documentId.endsWith(`_${pipelineConfigSuffix(config.entry.id)}`)
    )?.entry.id
  }, [documentId, configs])

  const documentLoadOptions = useMemo(() => {
    if (!bareId || !siteID) return undefined
    return systemDocumentsByDocumentIds([bareId], { site: siteID }, 50)
  }, [bareId, siteID])

  const { data: docsRaw, isLoading: isLoadingDocs } =
    useGetRolodexEntriesByLoadOptions<unknown>(documentLoadOptions)

  const docs = useMemo(
    () =>
      docsRaw === undefined ? undefined : parseSystemDocumentEntries(docsRaw),
    [docsRaw]
  )

  return useMemo(() => {
    const availableConfigIds = new Set<string>()
    for (const wrapper of docs ?? []) {
      const matchesBareId =
        getSystemDocumentBqId(wrapper) === bareId || wrapper.entry.id === bareId
      if (!matchesBareId) continue
      if (siteID && wrapper.entry.tags['site'] !== siteID) continue
      const configId = wrapper.entry.tags['pipeline-config']
      if (typeof configId === 'string' && configId.length > 0) {
        availableConfigIds.add(configId)
      }
    }

    const perConfig: ConfigAvailability[] = (configs ?? []).map((config) => ({
      configId: config.entry.id,
      status: config.entry.data?.status,
      isAvailable: availableConfigIds.has(config.entry.id),
    }))

    return {
      bareId,
      providedConfigId,
      perConfig,
      availableConfigs: perConfig.filter((row) => row.isAvailable),
      isLoading: isLoadingConfigs || isLoadingDocs,
    }
  }, [
    configs,
    docs,
    bareId,
    siteID,
    providedConfigId,
    isLoadingConfigs,
    isLoadingDocs,
  ])
}
